import clsx from "clsx";
import cookies from "js-cookie";
import get from "lodash/get";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";

import EmailAlert from "~/components/Layout/EmailAlert";
import Menu from "~/components/Layout/Navigation";
import AgreementPage from "~/pages/Agreement";
import GetCurrentDealerProvider from "~/pages/IvideonDealerRegistry/getCurrentDealer";
import useVersionContextRoutes from "~/versionControl/hooks/useVersionContextRoutes";
import Footer from "~/widgets/Footer";
import { useDealerActions } from "~/app/Context/DealerContext";
import { Header } from "~/components/Layout/Header";
import { NOTIFICATIONS } from "~/constans/notifications";
import { ROLE_TYPES } from "~/constans/roles";
import { STATUS_TYPES } from "~/constans/statuses";
import { useDealers } from "~/entity/Dealers";
import type { IMyDealers } from "~/entity/Dealers";
import { useSetLocationData } from "~/hooks/locationData";
import { ROUTES } from "~/Routes";
import {
  DealerStatus,
  useGetFileByHashLazyQuery,
  useGetMyDealersLazyQuery,
  useGetNotificationsQuery,
  useGetProfileQuery,
  // useListenReportReadySubscription,
  useUpdateNotificationMutation,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useUser } from "~/shared/context/UserContext";
import { useCurrentDealerId } from "~/shared/hooks/useCurrentDealerId";
import type { PermissionKey } from "~/types/Permissions";
import { IRoute } from "~/types/Routes";

import useGetStatusAuthorizedIvideon from "./hooks/useGetStatusAuthorizedIvideon";

import s from "./styles.less";

export const DefaultLayout = () => {
  const { dealersMy } = useDealers();
  const { t } = useTranslation();

  const { hasPermission, loading: loadingPermissions } = usePermissions();
  const setData = useSetLocationData();
  const { setDealerIdToContext } = useDealerActions();
  const currentDealerId = useCurrentDealerId();
  const currentDealerName = sessionStorage.getItem("currentDealerName");
  const currentDealerNameEn = sessionStorage.getItem("currentDealerNameEn");

  const [isSelectedDealer, setIsSelectedDealer] = useState(!!currentDealerId);
  const isSelectedDealerRef = useRef(isSelectedDealer);
  const [meProfile, setMeProfile] = useState({});
  const pendingDealerRef = useRef<{
    dealerId: string;
    dealerName: string;
    dealerNameEn: string;
  } | null>(null);
  const getProfile = useGetProfileQuery({});
  const history = useHistory();
  const location = useLocation();

  const fullWidthPaths = useMemo(() => {
    const pathname = location.pathname;
    const fullWidthRoutes = [
      "/staff/bonus",
      "/staff/training",
      "/staff/training/fact",
      "/staff/training/required",
      "/staff/training/completed",
      "/staff/employee/personal",
      "/staff/employee",
      "/dealers",
      "/dealers/",
      "/dnm",
      "/dnm/",
      "/violations",
      "/violations/",
      "/users",
      "/users/",
      "/editor/gr/",
      "/editor/q/",
      "/editor/staff/",
      "/base/staff",
      "/base/staff/",
      "/editor/subdivision/",
      "/editor/violation/",
      "/editor/zd",
      "/editor/zd/",
      "/editor/directories/pd",
      "/editor/directories/pd/",
      "/editor/directories/ed",
      "/editor/directories/ed/",
      "/editor/directories/combination-rules",
      "/editor/directories/combination-rules/",
      "/editor/cep/groups",
      "/editor/cep/groups/",
      "/editor/cep/levels",
      "/editor/cep/levels/",
      "/editor/cep/courses",
      "/editor/cep/courses/",
      "/editor/cep/ptp",
      "/editor/cep/ptp/",
      "/editor/doc",
      "/editor/doc/",
      "/editor/reports",
      "/editor/reports/",
      "/editor/camera-zones",
      "/editor/camera-zones/",
      "/editor/is/pd",
      "/editor/is/pd/",
      "/editor/is/dis",
      "/editor/is/dis/",
      "/editor/photo-gallery",
      "/editor/photo-gallery/",
      "/editor/address_selection_guide",
      "/editor/address_selection_guide/",
      "/editor/trusted_persons",
      "/editor/trusted_persons/",
      "/editor/ivideon-plans",
      "/editor/ivideon-plans/",
      "/editor/dnm-role-mappings",
      "/editor/dnm-role-mappings/",
      "/editor/dealer-metrics",
      "/editor/dealer-metrics/",
      "/editor/contest-territories",
      "/editor/contest-territories/",
      "/editor/requisites",
      "/editor/requisites/",
      "/editor/contract-templates",
      "/editor/contract-templates/",
      "/announcement",
      "/announcement/",
      "/documents",
      "/documents/",
      "/reports",
      "/reports/",
      "/gr",
      "/gr/",
      "/q",
      "/q/",
      "/staff",
      "/staff/",
      "/staff/appeals",
      "/staff/employee",
      "/dealers/list-of-documents",
      "/dealers/list-of-documents/",
      "/logs",
      "/logs/",
      "/logs/integrations",
      "/logs/ivideon",
      "/logs/email",
      "/ivideon/register-of-documents",
      "/ivideon/register-of-documents/history",
      "/ivideon/dealers",
      "/ivideon/dealers/",
      "/indicators",
      "/indicators/",
      "/uploads",
      "/uploads/",
      "/accounting",
      "/accounting/",
      "/camera-view",
      "/camera-view/",
      "/catalogApplications",
      "/catalogApplications/",
      "/reg_men",
      "/reg_men/",
      "/staff/licenses/partners",
      "/staff/licenses",
      "/staff/licenses/organization",
      "/staff/licenses/position-group",
      "/staff/licenses/requests",
      "/editor/base-tariffs",
    ];

    // Проверка на динамические пути (/editor/gr/:id и т.д.)
    const fullWidthDynamicPatterns = [
      /^\/editor\/gr\/\d+/,
      /^\/editor\/q\/\d+/,
      /^\/editor\/cep\/ptp\/\d+/,
      /^\/editor\/doc\/\d+/,
      /^\/documents\/sub_catalogs\/.+/,
      /^\/documents\/documents_in_catalog\/.+/,
      /^\/reports\/.+/,
      /^\/pkd\/.+/,
      /^\/checklists-department\/.+/,
      /^\/ivideon\/dealers\/.+/,
      /^\/ivideon\/cameras\/history\/[^/]+\/?$/,
      /^\/accounting\/.+/,
      /^\/camera-view\/.+/,
      /^\/catalogApplications\/.+/,
      /^\/administration\/directory\/.+/,
      /^\/base\/staff\/[^/]+\/?$/,
      /^\/base\/viewing\/[^/]+\/?$/,
    ];

    const isDynamicFullWidth = fullWidthDynamicPatterns.some((pattern) =>
      pattern.test(pathname),
    );

    return fullWidthRoutes.includes(pathname) || isDynamicFullWidth;
  }, [location.pathname]);
  const { dealerChoice } = useUser();

  const {
    isBonusPage,
    isEmployStaffPage,
    isEmployStaffPersonalPage,
    isRegisterRolePage,
    isTrainingPage,
  } = useMemo(() => {
    const pathname = location.pathname;
    return {
      isBonusPage: pathname === "/staff/bonus",
      isEmployStaffPersonalPage: pathname === "/staff/employee/personal",
      isEmployStaffPage: pathname === "/staff/employee",
      isTrainingPage:
        pathname === "/staff/training" ||
        pathname === "/staff/training/fact" ||
        pathname.match(/\/editor\/cep\/ptp\/(\d+)/) ||
        pathname === "/staff/training/required" ||
        pathname === "/staff/training/completed",
      isRegisterRolePage: pathname.includes("registry_of_role"),
    };
  }, [location.pathname]);

  const role = get(getProfile, "data.UserGetter.getProfile.role", "guest");

  const { data: dataStatusIvideon, isLoading } =
    useGetStatusAuthorizedIvideon(currentDealerId);

  useEffect(() => {
    const isNoCaching = [
      ROLE_TYPES.ADMIN.name,
      ROLE_TYPES.ADMIN_DATA.name,
    ].includes(role)
      ? "true"
      : "";
    sessionStorage.setItem("noCaching", isNoCaching);
  }, [role]);

  useEffect(() => {
    if (dataStatusIvideon) {
      sessionStorage.setItem(
        "IsIvideonAuthorized",
        dataStatusIvideon?.dealers[0]?.ivideonAuth ?? false,
      );
    }
  }, [dataStatusIvideon]);

  const routesBlockList = useVersionContextRoutes();

  const versionControlRouteBlockMiddleWear = (routes: IRoute[]) =>
    routes.filter((route) => !routesBlockList.includes(route.name));

  // PASDILER-2137: при отказе от соглашения перенаправляем на t-portal
  const handleDisagree = () => {
    sessionStorage.removeItem("currentDealerId");
    sessionStorage.removeItem("currentDealerName");
    sessionStorage.removeItem("currentDealerNameEn");
    cookies.remove("token");
    window.location.replace("https://tportal.vaz.ru/");
  };

  const routesByAccess: IRoute[] = useMemo(() => {
    if (loadingPermissions) return [];

    return ROUTES.filter((route) => {
      if (route.name === "DEALERS") {
        return dealerChoice !== "none";
      }

      // Если permissions нет или пустой массив — маршрут доступен всем
      if (!route?.permissions || route.permissions.length === 0) {
        return true;
      }

      return route.permissions.some((perm) => {
        if (perm.includes("&&")) {
          const allPerms = perm
            .split("&&")
            .map((p) => p.trim() as PermissionKey);

          return allPerms.every((p) => hasPermission(p));
        }

        return hasPermission(perm);
      });
    });
  }, [loadingPermissions, hasPermission, dealerChoice]);

  const handleGetMyDealers = async () => {
    try {
      const response = await dealersMy(10, 1);
      const data = response.data;
      const firstDealer = data.dealers.data[0];

      if (firstDealer) {
        const dealerId = firstDealer.id.toString();
        const dealerName = firstDealer.dealerName;
        const dealerNameEn = firstDealer.dealerName;
        if (isSelectedDealerRef.current) {
          setDealerIdToContext(dealerId);
          sessionStorage.setItem("currentDealerId", dealerId);
          sessionStorage.setItem("currentDealerName", dealerName);
          sessionStorage.setItem("currentDealerNameEn", dealerNameEn);
        } else {
          pendingDealerRef.current = { dealerId, dealerName, dealerNameEn };
        }
      } else {
        if (dealerChoice !== "none") alert(t("errors.no_dealers"));
      }
    } catch (error) {
      console.error("Ошибка при получении дилеров:", error);
    }
  };

  useEffect(() => {
    if ((!currentDealerId || !currentDealerName) && dealerChoice) {
      handleGetMyDealers();
    }
  }, [dealerChoice]);

  const [updateNotificationMutation] = useUpdateNotificationMutation({});
  const [GetFileByHash, FileByHash] = useGetFileByHashLazyQuery({
    fetchPolicy: "no-cache",
    onCompleted(data) {
      const { link, name, hash } = get(data, "FileGetter.getFileByHash") || {};
      if (hash && link) {
        updateNotificationMutation({
          variables: {
            id: hash,
            status: STATUS_TYPES.CONFIRM,
            content: link,
            label: name,
          },
        });
        document.location.href = link;
        clearInterval(fileHashes[hash]);
        setTimeout(() => {
          closeNotification(hash);
        }, 10000);
      }
    },
  });

  const closeNotification = (hash) => {
    updateNotificationMutation({
      variables: {
        id: hash,
        isOpen: false,
      },
    });
  };

  // ---------------------------If sockets is not work! Begin!-----------------------------------
  const {
    data: { getNotifications },
    refetch,
  } = useGetNotificationsQuery();

  const notifications = getNotifications.filter(
    ({ type, isOpen }) => type === NOTIFICATIONS.report && isOpen,
  );

  const [fileHashes, seFileHashes] = useState({});

  /**
   * Если загрузка файла должна ожидаться с сервера очень долго,
   * добавь сюда адрес страницы, где это должно происходить
   * */
  const longTimeLoadingPagesCollection = [
    "/uploads/",
    "/staff/",
    "/zd/main/",
    "/zd/plan_report/",
    "/srr/",
    "/base/staff",
    "/base/staff/",
    "/reports/",
    "/reports/view/",
    "/logs/email",
    "/staff/bonus",
    "/staff/training",
    "/ivideon/summary",
    "/violations",
    "/violations/",
    "/ivideon/dealers",
    "/ivideon/dealers/",
  ];

  useEffect(() => {
    notifications.length > 0 &&
      !FileByHash.loading &&
      fileHashes &&
      notifications.map(({ id }) => {
        if (!fileHashes[id]) {
          const setIntervalId = setInterval(() => {
            GetFileByHash({
              variables: {
                hash: id,
              },
            });
          }, 2000);

          seFileHashes({
            ...fileHashes,
            [id]: setIntervalId,
          });
          setTimeout(
            () => {
              clearInterval(setIntervalId);
              updateNotificationMutation({
                variables: {
                  id,
                  status: STATUS_TYPES.ERROR,
                },
              });
              setTimeout(() => {
                closeNotification(id);
              }, 5000);
            },
            longTimeLoadingPagesCollection.includes(location.pathname)
              ? 72000000
              : 18000,
          );
        }
      });
    refetch();
  }, [notifications.length]);
  // const isNewYear = Date.now() > Date.parse('2025-04-30T00:00:00.000+04:00')
  const isNewDate = Date.now() > Date.parse("2025-05-01T00:00:00.000+03:00");
  const isCloseDate = Date.now() > Date.parse("2025-05-02T00:00:00.000+03:00");
  // ---------------------------If sockets is not work! End!-----------------------------------
  return (
    <GetCurrentDealerProvider>
      <div className={clsx(s.mainLayout)}>
        <div className={s.page}>
          <Header />
          {!isCloseDate && (
            <div className={s.wrapper}>
              <div className={s.description}>
                {isNewDate
                  ? 'Уважаемые пользователи! Проводятся технические работы по нагрузочному тестированию. Возможно замедление работы сервиса ИС "Паспорт Дилера" в период с 00:00 до 23:59 со временем недоступности до 2 часов в течении дня. Приносим извинения за неудобства.'
                  : 'Уважаемые пользователи! 01.05.2025 будут проводиться технические работы в ИС "Паспорт Дилера". Возможно замедление работы сервиса со временем недоступности до 2 часов в течении дня для проведения нагрузочного тестирования. Завершите работы до периода недоступности. Благодарим за понимание.'}
              </div>
            </div>
          )}
          <div
            className={clsx(s.container, {
              [s.back]: isRegisterRolePage,
            })}
          >
            <div
              className={clsx(s.content, {
                [s.content100]: fullWidthPaths,
              })}
            >
              <Switch>
                <Route
                  path="/agreement"
                  render={() => {
                    const params = new URLSearchParams(history.location.search);
                    const returnUrl = params.get("returnUrl") || "/";

                    return (
                      <AgreementPage
                        onAgree={() => {
                          isSelectedDealerRef.current = true;
                          if (pendingDealerRef.current) {
                            const { dealerId, dealerName, dealerNameEn } =
                              pendingDealerRef.current;
                            setDealerIdToContext(dealerId);
                            sessionStorage.setItem("currentDealerId", dealerId);
                            sessionStorage.setItem(
                              "currentDealerName",
                              dealerName,
                            );
                            sessionStorage.setItem(
                              "currentDealerNameEn",
                              dealerNameEn,
                            );
                            pendingDealerRef.current = null;
                          }
                          setIsSelectedDealer(true);
                          history.replace(returnUrl);
                        }}
                        onDisagree={handleDisagree}
                      />
                    );
                  }}
                />
                {(isSelectedDealer ||
                  dealerChoice === "none" ||
                  hasPermission("candidateSelection.candidates.get")) &&
                  versionControlRouteBlockMiddleWear(routesByAccess).map(
                    (route) => {
                      if (route.isRedirect) {
                        if (
                          role === "autovaz" ||
                          (!hasPermission("passport.get") &&
                            hasPermission("announcements.get"))
                        ) {
                          return (
                            <Redirect
                              key={`redirect_announcement`}
                              to={"/announcement/"}
                            />
                          );
                        }
                        return <React.Fragment key={`redirect_${route.path}`} />;
                      }
                      if (
                        (route.path === "/" || route.path === "") &&
                        role === "autovaz"
                      ) {
                        return (
                          <Redirect
                            key={`redirect_announcement`}
                            to={"/announcement/"}
                          />
                        );
                      }
                      return (
                        <Route
                          exact={route.exact}
                          path={route.path}
                          key={`route_${route.path}`}
                          component={route.pageComponent}
                        />
                      );
                    },
                  )}
                {!isSelectedDealer &&
                  dealerChoice &&
                  dealerChoice !== "none" && (
                    <Redirect
                      to={`/agreement?returnUrl=${encodeURIComponent(
                        location.pathname !== "/agreement"
                          ? location.pathname + (location.search || "")
                          : "/",
                      )}`}
                    />
                  )}
              </Switch>
            </div>
          </div>
        </div>
        <EmailAlert />
        <Footer />
      </div>
    </GetCurrentDealerProvider>
  );
};
